import { AxiosError } from 'axios';
import { getUserName } from '../chatUtils';

/**
 * Error handling utilities for API services
 */
export const errorHandlers = {
    /**
     * Extract error message from API error response
     */
    getErrorMessage: (error: any): string => {
        if (error && typeof error === 'object') {
            // Check if it's an Axios error
            const axiosError = error as AxiosError<any>;

            if (axiosError.response?.data?.message) {
                return axiosError.response.data.message;
            }

            if (axiosError.response?.data?.error) {
                return axiosError.response.data.error;
            }

            if ('message' in error && typeof error.message === 'string') {
                return error.message;
            }
        }

        return 'An unknown error occurred';
    },

    /**
     * Check if error is a network error
     */
    isNetworkError: (error: any): boolean => {
        return !error.response && error.request;
    },

    /**
     * Check if error is an authentication error
     */
    isAuthError: (error: AxiosError): boolean => {
        return error.response?.status === 401 || error.response?.status === 403;
    }
};

/**
 * Data transformation utilities
 */
export const transformers = {
    /**
     * Convert server date strings to Date objects
     * @param data Object containing date strings
     * @param dateFields Fields that should be converted to Date objects
     */
    parseDates: <T extends Record<string, any>>(data: T, dateFields: string[]): T => {
        const parsedData: any = { ...data };

        dateFields.forEach((field: any) => {
            if (parsedData[field] && typeof parsedData[field] === 'string') {
                parsedData[field] = new Date(parsedData[field]);
            }
        });

        return parsedData;
    },

    /**
     * Convert Date objects to ISO strings for API requests
     */
    stringifyDates: <T extends Record<string, any>>(data: T, dateFields: string[]): T => {
        const stringifiedData: any = { ...data };

        dateFields.forEach((field: any) => {
            if (stringifiedData[field] instanceof Date) {
                stringifiedData[field] = (stringifiedData[field] as Date).toISOString();
            }
        });

        return stringifiedData;
    }
};

/**
 * Service helper functions
 */
export const serviceHelpers = {
    /**
     * Create pagination parameters for API requests
     */
    paginationParams: (page: number, limit: number) => ({
        page,
        limit
    }),

    /**
     * Format query parameters for API requests
     */
    formatQueryParams: (params: Record<string, any>) => {
        return Object.entries(params)
            .filter(([_, value]) => value !== undefined && value !== null)
            .reduce((acc, [key, value]) => {
                acc[key] = Array.isArray(value) ? value.join(',') : value;
                return acc;
            }, {} as Record<string, any>);
    }
};

/**
 * Session ID utilities
 */
export const sessionIdUtils = {
    getSessionId: () => {
        const sessionId = localStorage.getItem('ozChatSessionId');
        if (!sessionId) {
            const userName = getUserName();
            if (userName) {
                localStorage.setItem('ozChatSessionId', `${userName}&&AgentSubUserChat`);
                return `${userName}&&AgentSubUserChat`;
            }
        }
        return sessionId;
    },
};

export default {
    errorHandlers,
    transformers,
    serviceHelpers,
    sessionIdUtils
}; 